import React, { useEffect, useState } from 'react';
import { getFights, getCommunityStats } from '../services/api';
import CountdownTimer from './CountdownTimer';
import '../styles/app/dashboard.css';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Split "Alexander Volkanovski" into a small first line and a big last name,
// the way fight posters set names.
const splitName = (name) => {
  if (!name) return ['', ''];
  const parts = name.trim().split(' ');
  if (parts.length === 1) return ['', parts[0]];
  return [parts.slice(0, -1).join(' '), parts[parts.length - 1]];
};

function MainEventHero({ event }) {
  const [mainEvent, setMainEvent] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);

  useEffect(() => {
    if (!event?.id) return;
    let cancelled = false;
    Promise.all([
      getFights(event.id).catch(() => ({ data: [] })),
      getCommunityStats(event.id).catch(() => ({ data: [] }))
    ]).then(([fightsRes, statsRes]) => {
      if (cancelled) return;
      const fights = (fightsRes.data || []).filter(f => !f.is_cancelled);
      // Main event: lowest fight_order on main card (UFC main event = order 1)
      const main = fights.filter(f => f.card_type === 'main')
        .sort((a, b) => (a.fight_order || 0) - (b.fight_order || 0))[0];
      setMainEvent(main || null);
      if (main) {
        const fightStats = (statsRes.data || []).find(s => s.fight_id === main.id);
        setCommunityStats(fightStats || null);
      }
    });
    return () => { cancelled = true; };
  }, [event?.id]);

  const isLocked = event?.status === 'locked';
  const isCompleted = event?.status === 'completed';
  // A locked event only counts as "live" once its date has arrived — picks
  // can be locked ahead of fight day, and that shouldn't read as LIVE NOW.
  const isLive = isLocked && event?.date && new Date(event.date) <= new Date();

  // Status badge
  const statusBadge = isLive ? 'LIVE NOW' : isLocked ? 'PICKS CLOSED' : isCompleted ? 'EVENT COMPLETED' : 'NEXT UP';
  const statusTone = isLive ? 'live' : isLocked ? 'locked' : isCompleted ? 'done' : 'next';
  const isTitle = mainEvent?.is_title_fight === 1;

  const p1 = communityStats?.fighter1_percentage;
  const p2 = communityStats?.fighter2_percentage;
  const hasSplit = typeof p1 === 'number' && typeof p2 === 'number' && (p1 + p2) > 0;

  return (
    <section className={`dash-hero ${isTitle ? 'dash-hero-title' : ''} ${isLive ? 'dash-hero-live' : ''}`}>
      {/* Arena backdrop: stage glow, claw slashes, cage outline */}
      <div className="dash-hero-bg" aria-hidden="true">
        <span className="dash-hero-glow dash-hero-glow-red" />
        <span className="dash-hero-glow dash-hero-glow-blue" />
        <span className={`pnp-claw dash-hero-claw ${isTitle ? 'pnp-claw-gold' : ''}`} />
        <span className="dash-hero-cage" />
      </div>

      <div className="dash-hero-inner">
        {/* Top status bar */}
        <div className="dash-hero-top">
          <span className={`dash-hero-status dash-hero-status-${statusTone}`}>
            {isLocked && <span className="pnp-live-dot" />}
            {statusBadge}
          </span>
          {isTitle && (
            <span className="pnp-chip pnp-chip-gold dash-hero-belt">
              <img src="/images/title-belt.svg" alt="" width="14" height="14" />
              Title Fight
            </span>
          )}
        </div>

        {/* Event name + date */}
        <div className="dash-hero-head">
          <h2 className="dash-hero-event">{event.name}</h2>
          <p className="dash-hero-date">
            {new Date(event.date).toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric'
            })}
          </p>
        </div>

        {/* Fighter face-off */}
        {mainEvent ? (
          <>
            <div className="dash-faceoff">
              <FighterSide
                corner="red"
                name={mainEvent.fighter1_name}
                image={mainEvent.fighter1_image_url}
                record={mainEvent.fighter1_record}
                percentage={communityStats?.fighter1_percentage}
              />

              {/* VS Center */}
              <div className="dash-faceoff-mid">
                <span className="pnp-vs dash-faceoff-vs">VS</span>
              </div>

              <FighterSide
                corner="blue"
                name={mainEvent.fighter2_name}
                image={mainEvent.fighter2_image_url}
                record={mainEvent.fighter2_record}
                percentage={communityStats?.fighter2_percentage}
              />
            </div>

            {mainEvent.weight_class && (
              <p className="dash-faceoff-weight">{mainEvent.weight_class}{isTitle ? ' Championship' : ''}</p>
            )}

            {hasSplit && (
              <div className="dash-split" aria-label={`Community picks: ${p1}% vs ${p2}%`}>
                <span className="dash-split-num dash-split-num-red">{p1}%</span>
                <div className="dash-split-track">
                  <span className="dash-split-red" style={{ width: `${(p1 / (p1 + p2)) * 100}%` }} />
                  <span className="dash-split-blue" style={{ width: `${(p2 / (p1 + p2)) * 100}%` }} />
                </div>
                <span className="dash-split-num dash-split-num-blue">{p2}%</span>
              </div>
            )}
          </>
        ) : (
          <div className="dash-hero-tba">Card to be announced</div>
        )}

        {/* Bottom: countdown or community stats */}
        {(event.status === 'open' ||
          (isLocked && communityStats && communityStats.total_picks > 0) ||
          (isCompleted && mainEvent?.winner)) && (
          <div className="dash-hero-foot">
            {event.status === 'open' && (
              <CountdownTimer targetDate={event.date} label="Picks lock in" />
            )}
            {isLocked && communityStats && communityStats.total_picks > 0 && (
              <p className="dash-hero-note">
                <span className="pnp-num">{communityStats.total_picks.toLocaleString()}</span> picks made on this fight
              </p>
            )}
            {isCompleted && mainEvent?.winner && (
              <p className="dash-hero-note dash-hero-winner">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                Winner: {mainEvent.winner}{mainEvent.method ? ` via ${mainEvent.method}` : ''}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function FighterSide({ name, image, record, percentage, corner }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [first, last] = splitName(name);
  const favored = typeof percentage === 'number' && percentage >= 60;
  return (
    <div className={`dash-fighter dash-fighter-${corner}`}>
      <div className="dash-fighter-shot">
        <span className="dash-fighter-halo" aria-hidden="true" />
        {image && !imgFailed ? (
          <img
            src={image}
            alt={name}
            loading="eager"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span className="dash-fighter-initials">{getInitials(name)}</span>
        )}
      </div>
      <p className="dash-fighter-name">
        {first && <span className="dash-fighter-first">{first}</span>}
        <span className="dash-fighter-last">{last}</span>
      </p>
      {record && <p className="dash-fighter-record">{record}</p>}
      {typeof percentage === 'number' && percentage > 0 && (
        <p className={`dash-fighter-pct ${favored ? 'is-favored' : ''}`}>
          {percentage}% pick
        </p>
      )}
    </div>
  );
}

export default MainEventHero;
